import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Clock, CalendarX, Plus, Pencil, Trash2, Info, CalendarDays } from 'lucide-react';
import { TimeRulesCalendar } from '../components/settings/TimeRulesCalendar';
import { confirmDialog } from '../components/ConfirmHost';
import { TimeRuleFormDialog } from '../components/settings/TimeRuleFormDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : e.response?.data?.detail?.[0]?.msg?.replace('Value error, ', '') || 'İşlem başarısız');

const INFO = {
  creation: {
    icon: Clock, title: 'A) Talep Oluşturma Zamanı',
    text: 'Firmanın talebi hangi zamanda OLUŞTURABİLECEĞİNİ sınırlar. Kontrol, firmanın o anki yerel saatine (Türkiye) göre yapılır; talepteki çalışma tarihiyle ilgisi yoktur.',
    examples: ['Hafta içi 16:00–23:59 → saat 16:30\'da firma "Yeni Talep" diyemez, taslağı da incelemeye gönderemez.', 'Hafta sonu 00:00–23:59 → cumartesi/pazar hiç talep oluşturulamaz.', '20.12–02.01 tarih aralığı → yılbaşı döneminde talep oluşturulamaz.'],
  },
  work: {
    icon: CalendarX, title: 'B) Çalışma Zamanı',
    text: 'Talepte SEÇİLEN çalışma tarih ve saatini sınırlar. Talebin hangi gün oluşturulduğuyla ilgisi yoktur; firma Bölüm 1\'de yasak saate denk gelen bir çalışma seçerse uyarılır ve devam edemez.',
    examples: ['Hafta içi 10:00–22:00 → AVM açıkken çalışma girilemez; 08:00–11:00 seçen firma uyarı alır.', 'Bayram tarih aralığı → bu günlere denk gelen çalışma talebi girilemez.', 'Sadece "Mağaza / Sıcak İş" için hafta sonu 00:00–23:59 → o iş çeşidi hafta sonu yapılamaz.'],
  },
};

const RuleCard = ({ r, locations, onEdit, onDelete, onToggle }) => {
  const locs = r.location_ids.map(id => locations.find(l => l.id === id)?.name).filter(Boolean);
  return (
    <Card className={`border-slate-200 transition-shadow hover:shadow-md ${r.is_active ? '' : 'opacity-60'}`} data-testid={`rule-card-${r.id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <p className="font-semibold text-slate-900" data-testid={`rule-summary-${r.id}`}>{r.auto_message}</p>
          <div className="flex items-center gap-1 shrink-0">
            <Switch checked={r.is_active} onCheckedChange={(v) => onToggle(r, v)} data-testid={`rule-toggle-${r.id}`} />
            <Button size="icon" variant="ghost" className="text-blue-600" onClick={() => onEdit(r)} data-testid={`rule-edit-${r.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => onDelete(r)} data-testid={`rule-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 text-xs">
          <Badge variant="secondary" className="font-normal">Lokasyon: {locs.length ? locs.join(', ') : 'Tümü'}</Badge>
          <Badge variant="secondary" className="font-normal">İş tipi: {r.is_tipi.length ? r.is_tipi.join(', ') : 'Tümü'}</Badge>
          <Badge variant="secondary" className="font-normal">İş çeşidi: {r.is_cesidi.length ? r.is_cesidi.join(', ') : 'Tümü'}</Badge>
        </div>
        {r.message && <p className="text-sm text-slate-600">Firma uyarısı: "{r.message}"</p>}
        {r.note && <p className="text-xs text-slate-400">Not: {r.note}</p>}
      </CardContent>
    </Card>
  );
};

const RuleTab = ({ kind, rules, locations, onNew, ...h }) => {
  const info = INFO[kind];
  const Icon = info.icon;
  const list = rules.filter(r => r.kind === kind);
  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-4 ${kind === 'creation' ? 'border-sky-200 bg-sky-50/60' : 'border-amber-200 bg-amber-50/60'}`} data-testid={`rule-info-${kind}`}>
        <p className="font-semibold text-slate-900 flex items-center gap-2"><Icon className="h-4 w-4" />{info.title}</p>
        <p className="text-sm text-slate-700 mt-1">{info.text}</p>
        <ul className="mt-2 space-y-1 text-sm text-slate-600">{info.examples.map(e => <li key={e} className="flex gap-2"><Info className="h-3.5 w-3.5 mt-1 shrink-0 text-slate-400" />Örnek: {e}</li>)}</ul>
      </div>
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{list.length} kural · İSG Uzmanı ve Süper Admin bu kurallardan muaftır</p>
        <Button onClick={onNew} className="bg-slate-900 hover:bg-slate-800" data-testid={`rule-add-${kind}`}><Plus className="h-4 w-4 mr-1" />Yeni Kural</Button>
      </div>
      {list.length === 0
        ? <Card className="border-dashed"><CardContent className="p-8 text-center text-slate-500" data-testid={`rules-empty-${kind}`}>Henüz kural yok.</CardContent></Card>
        : <div className="grid lg:grid-cols-2 gap-3">{list.map(r => <RuleCard key={r.id} r={r} locations={locations} {...h} />)}</div>}
    </div>
  );
};

export default function TimeRulesPage() {
  const { user, canView, canTab } = useAuth();
  const [rules, setRules] = useState([]);
  const [locations, setLocations] = useState([]);
  const [workAreas, setWorkAreas] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(`${API}/time-rules`).then(r => setRules(r.data)).catch(() => toast.error('Kurallar yüklenemedi'));
  useEffect(() => {
    if (!canView('time_rules')) return;
    load();
    axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {});
    axios.get(`${API}/work-areas`).then(r => setWorkAreas(r.data)).catch(() => {});
  }, [user]);

  const strip = ({ id, auto_message, created_at, created_by, updated_at, ...rest }) => rest;
  const save = async (data) => {
    try {
      if (editing.item?.id) await axios.put(`${API}/time-rules/${editing.item.id}`, strip(data));
      else await axios.post(`${API}/time-rules`, strip(data));
      toast.success('Kural kaydedildi');
      setEditing(null);
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const toggle = async (r, v) => { try { await axios.put(`${API}/time-rules/${r.id}`, strip({ ...r, is_active: v })); load(); } catch (e) { toast.error(errText(e)); } };
  const remove = async (r) => {
    if (!(await confirmDialog({ title: 'Kural silinsin mi?', description: r.auto_message, confirmLabel: 'Evet, Sil' }))) return;
    try { await axios.delete(`${API}/time-rules/${r.id}`); toast.success('Kural silindi'); load(); } catch (e) { toast.error(errText(e)); }
  };

  if (!canView('time_rules')) return <p className="text-slate-500">Bu sayfa sadece Süper Admin kullanıcılar içindir.</p>;
  const h = { rules, locations, onEdit: (item) => setEditing({ kind: item.kind, item }), onDelete: remove, onToggle: toggle };

  return (
    <div className="space-y-6 w-full" data-testid="time-rules-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Clock className="h-6 w-6 text-sky-600" />Talep Zaman Kuralları</h1>
        <p className="text-slate-500 mt-1">İki ayrı kural türü vardır: <b>A)</b> firmanın talebi ne zaman oluşturabileceği, <b>B)</b> talepte hangi tarih/saatte çalışma yapılabileceği.</p>
      </div>
      <Tabs defaultValue={['creation', 'work', 'calendar'].find(t => canTab('time_rules', t))} className="space-y-4">
        <TabsList data-testid="time-rule-tabs">
          {canTab('time_rules', 'creation') && <TabsTrigger value="creation" data-testid="time-rule-tab-creation"><Clock className="h-4 w-4 mr-1" />A) Talep Oluşturma</TabsTrigger>}
          {canTab('time_rules', 'work') && <TabsTrigger value="work" data-testid="time-rule-tab-work"><CalendarX className="h-4 w-4 mr-1" />B) Çalışma Zamanı</TabsTrigger>}
          {canTab('time_rules', 'calendar') && <TabsTrigger value="calendar" data-testid="time-rule-tab-calendar"><CalendarDays className="h-4 w-4 mr-1" />Takvim</TabsTrigger>}
        </TabsList>
        <TabsContent value="creation"><RuleTab kind="creation" onNew={() => setEditing({ kind: 'creation', item: null })} {...h} /></TabsContent>
        <TabsContent value="calendar"><TimeRulesCalendar rules={rules} locations={locations} onEdit={h.onEdit} /></TabsContent>
        <TabsContent value="work"><RuleTab kind="work" onNew={() => setEditing({ kind: 'work', item: null })} {...h} /></TabsContent>
      </Tabs>
      <TimeRuleFormDialog open={!!editing} kind={editing?.kind} item={editing?.item} locations={locations} workAreas={workAreas} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}
